// app/frontend/components/EventForm/utils/formHelpers.ts

/**
 * Generates a URL-friendly slug from a string
 */
export function generateSlug(input: string): string {
  return input
    .toLowerCase()
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/[^a-z0-9-]/g, '') // Remove all characters except lowercase letters, numbers, and hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with a single hyphen
    .replace(/^-|-$/g, ''); // Remove leading and trailing hyphens
}

/**
 * Converts array of numbers to array of strings (for MultiSelect values)
 */
export function numbersToStrings(numbers: number[]): string[] {
  return Array.isArray(numbers) ? numbers.map((id) => id.toString()) : [];
}

/**
 * Converts array of strings to array of numbers (from MultiSelect values)
 */
export function stringsToNumbers(strings: string[]): number[] {
  return Array.isArray(strings) ? strings.map((str) => Number(str)) : [];
}

/**
 * Formats a date string to YYYY-MM-DD format
 */
export function formatDateForInput(date: Date | string): string {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Parses date string safely for DatePickerInput
 */
export function parseDateSafely(dateString: string): Date | null {
  if (!dateString) return null;

  // If the value is just a date string (YYYY-MM-DD), parse it properly in local timezone
  if (dateString.length === 10 && dateString.includes('-')) {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day, 12, 0, 0); // Set to noon to avoid timezone issues
  }

  // Otherwise, use the standard Date constructor
  return new Date(dateString);
}

/**
 * Checks if a timezone value is valid (not empty or array)
 */
export function getValidTimezone(timezone: string | string[]): string {
  if (Array.isArray(timezone)) {
    return timezone[0] || 'UTC';
  }
  return timezone || 'UTC';
}

/**
 * Sorts an array of objects by name property
 */
export function sortByName<T extends { name: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Sorts event frequencies in specific order
 */
export function sortEventFrequencies<T extends { name: string }>(frequencies: T[]): T[] {
  const order = ['One Time', 'Weekly', 'Every Two Weeks', 'Monthly', 'Annual'];
  return [...frequencies].sort((a, b) => {
    const aIndex = order.indexOf(a.name);
    const bIndex = order.indexOf(b.name);

    // If both are in the order array, sort by index
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }

    // If only one is in the order array, prioritize it
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;

    // If neither is in the order array, sort alphabetically
    return a.name.localeCompare(b.name);
  });
}

/**
 * Filters organizers based on user permissions
 */
export function filterUserOrganizers(
  organizers: Array<{ id: number; name: string }>,
  user: { roles?: string[]; organizer_ids?: number[] } | null,
): Array<{ id: number; name: string }> {
  if (!user) return [];

  if (user.roles?.includes('SuperAdmin')) {
    return organizers;
  }

  return organizers.filter((org) => user.organizer_ids?.includes(org.id));
}

/**
 * Builds FormData for event submission
 */
export function buildEventFormData(
  formValues: any,
  submitType: 'draft' | 'publish',
  options: {
    csrfToken: string;
    isEditing?: boolean;
    editor?: any;
    croppedImage?: File | null;
  },
): FormData {
  const formData = new FormData();

  // Submission flags
  if (submitType === 'draft') {
    formData.append('save_draft', 'true');
  } else {
    formData.append('submit_for_approval', 'true');
    if (options.isEditing) {
      formData.append('event[event_status_id]', '2');
    }
  }

  // Basic fields
  formData.append('event[name]', formValues.name || '');
  formData.append('event[slug]', formValues.slug || '');
  formData.append('event[address]', formValues.address || '');
  formData.append('event[timezone]', getValidTimezone(formValues.timezone));

  // Description from rich text editor
  if (options.editor?.getHTML) {
    formData.append('event[description]', options.editor.getHTML());
  }

  // Images
  if (formValues.event_image) {
    formData.append('event[event_image]', formValues.event_image);
  }
  if (options.croppedImage) {
    formData.append('event[cropped_image]', options.croppedImage);
  }

  // Booleans
  formData.append('event[online_event]', formValues.online_event === 'true' ? 'true' : 'false');
  formData.append('event[is_recurring]', formValues.is_recurring === 'true' ? 'true' : 'false');
  formData.append('event[spans_next_day]', formValues.spans_next_day === 'true' ? 'true' : 'false');

  // Arrays
  formValues.event_type_ids?.forEach((id: number) => formData.append('event[event_type_ids][]', id.toString()));
  formValues.dance_style_ids?.forEach((id: number) => formData.append('event[dance_style_ids][]', id.toString()));
  formValues.artistIds?.forEach((id: number) => formData.append('event[artist_ids][]', id.toString()));
  formValues.organizer_ids?.forEach((id: number) => formData.append('event[organizer_ids][]', id.toString()));

  // URLs
  const urlFields = ['website_url', 'ticket_url', 'facebook_url', 'instagram_url', 'meetup_url', 'tiktok_url'];
  urlFields.forEach((field) => {
    const value = formValues[field];
    if (value) formData.append(`event[${field}]`, value);
  });

  // Missing city information
  if (formValues.missing_city_detected) {
    formData.append('event[missing_city_detected]', 'true');
    formData.append('event[missing_city_name]', formValues.missing_city_name || '');
  } else {
    formData.append('event[missing_city_detected]', 'false');
  }

  return formData;
}
